import React, { useMemo } from 'react';
import { Check, Copy, FileWarning, Image as ImageIcon, Info, Loader2, RefreshCw } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const shortId = (id) => (id ? `...${String(id).slice(-8)}` : '-');

const getHealth = (caseItem) => caseItem.health || {};
const getCaseId = (caseItem) => caseItem.case_id || caseItem.id;
const getVictimName = (caseItem) => caseItem.victim?.name || caseItem.victim_name || 'Unknown victim';

const signalMatches = (caseItem, filters) => {
  const health = getHealth(caseItem);
  const generationFailures = Number(health.generation_failure_count || 0);
  const imageTotal = Number(health.image_total || 0);
  const imageFailures = Number(health.image_failures || 0);

  if (filters.has_failures === 'true' && generationFailures <= 0) return false;
  if (filters.has_failures === 'false' && generationFailures > 0) return false;
  if (filters.has_images === 'true' && imageTotal <= 0 && imageFailures <= 0) return false;
  if (filters.has_images === 'false' && (imageTotal > 0 || imageFailures > 0)) return false;

  return true;
};

export const AdminReviewQueue = ({
  cases = [],
  total = 0,
  loading = false,
  filters = {},
  onFiltersChange,
  onRefresh,
  onViewCaseInfo,
  onCopyId,
  copiedId,
}) => {
  const visibleCases = useMemo(
    () => cases.filter((caseItem) => signalMatches(caseItem, filters)),
    [cases, filters]
  );

  const updateQuickFilter = (key) => {
    const nextValue = filters[key] === 'true' ? '' : 'true';
    onFiltersChange?.({
      ...filters,
      [key]: nextValue,
      offset: 0,
    });
  };

  const clearQuickFilters = () => {
    onFiltersChange?.({
      ...filters,
      has_failures: '',
      has_images: '',
      offset: 0,
    });
  };

  const hasQuickFilters = Boolean(filters.has_failures || filters.has_images);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Review Queue</h2>
          <p className="text-sm text-gray-400">
            {loading ? 'Loading review cases...' : `${visibleCases.length} shown from ${total || cases.length} non-clear review cases`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={filters.has_failures === 'true' ? 'default' : 'outline'}
            size="sm"
            onClick={() => updateQuickFilter('has_failures')}
            className="border-gray-700"
          >
            <FileWarning className="h-4 w-4" />
            Failures
          </Button>
          <Button
            variant={filters.has_images === 'true' ? 'default' : 'outline'}
            size="sm"
            onClick={() => updateQuickFilter('has_images')}
            className="border-gray-700"
          >
            <ImageIcon className="h-4 w-4" />
            Images
          </Button>
          {hasQuickFilters && (
            <Button variant="ghost" size="sm" onClick={clearQuickFilters} className="text-gray-400 hover:text-white">
              Clear
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={loading}
            className="border-gray-700 text-gray-200"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-gray-700 bg-gray-900/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-gray-700 bg-gray-800/60">
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Case</th>
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Severity</th>
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Issue Tags</th>
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Signals</th>
                <th className="p-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Theme</th>
                <th className="p-3 text-right text-xs font-medium uppercase tracking-wide text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {loading ? (
                <tr>
                  <td colSpan="6" className="p-8 text-center text-gray-400">
                    <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin" />
                    Loading review queue
                  </td>
                </tr>
              ) : visibleCases.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-8 text-center text-gray-500">
                    No non-clear review cases match the active quick filters.
                  </td>
                </tr>
              ) : visibleCases.map((caseItem) => {
                const caseId = getCaseId(caseItem);
                const health = getHealth(caseItem);
                const generationFailures = Number(health.generation_failure_count || 0);
                const imageFailures = Number(health.image_failures || 0);
                const imageTotal = Number(health.image_total || 0);

                return (
                  <tr key={caseId} className="hover:bg-gray-800/40 transition-colors">
                    <td className="p-3 align-top">
                      <div className="font-medium text-white">{getVictimName(caseItem)}</div>
                      <button
                        type="button"
                        onClick={() => caseId && onCopyId?.(caseId, 'case')}
                        className="mt-1 flex items-center gap-1 font-mono text-xs text-gray-500 hover:text-gray-300"
                        title={caseId || ''}
                      >
                        {shortId(caseId)}
                        {copiedId === caseId ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                      </button>
                    </td>
                    <td className="p-3 align-top">
                      <Badge className="bg-amber-900/50 text-amber-300 border border-amber-700">
                        {health.review_severity || 'unspecified'}
                      </Badge>
                    </td>
                    <td className="p-3 align-top">
                      <div className="flex max-w-[320px] flex-wrap gap-1">
                        {(health.review_issue_tags || []).length > 0 ? (
                          health.review_issue_tags.map((tag) => (
                            <Badge key={tag} variant="outline" className="border-gray-700 text-gray-400 text-xs">
                              {tag.replace(/_/g, ' ')}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-sm text-gray-500">No tags</span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 align-top">
                      <div className="flex flex-wrap gap-1">
                        <Badge className={generationFailures > 0
                          ? 'bg-red-900/50 text-red-300 border border-red-700'
                          : 'bg-gray-800 text-gray-400 border border-gray-700'}
                        >
                          Failures {generationFailures}
                        </Badge>
                        <Badge className={imageFailures > 0
                          ? 'bg-orange-900/50 text-orange-300 border border-orange-700'
                          : 'bg-gray-800 text-gray-400 border border-gray-700'}
                        >
                          Images {imageFailures}/{imageTotal}
                        </Badge>
                      </div>
                    </td>
                    <td className="p-3 align-top text-sm capitalize text-gray-300">
                      {caseItem.theme?.replace(/_/g, ' ') || 'unknown'}
                    </td>
                    <td className="p-3 align-top">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onViewCaseInfo?.(caseId, caseItem.session_id)}
                          disabled={!caseId}
                          className="h-8 px-2 text-amber-400 hover:text-amber-300 hover:bg-amber-900/30"
                          title="View case info"
                        >
                          <Info className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => caseId && onCopyId?.(caseId, 'case')}
                          disabled={!caseId}
                          className="h-8 px-2 text-gray-400 hover:text-white hover:bg-gray-700"
                          title="Copy case ID"
                        >
                          {copiedId === caseId ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminReviewQueue;
